/** IndexedDB のレコードと、OneDrive 上のJSONの相互変換。 */

import type { DailySummary, Entry, MonthKey, TaskEntry } from '../domain/types';
import { SCHEMA_VERSION } from '../domain/types';
import type { DailyRecord, EntryRecord, TaskEntryRecord } from '../db/schema';

/** クラウドへ書き出す形（ローカル専用のフィールドを落とす）。 */
export function toCloudEntry(rec: Entry): Entry {
  return {
    schemaVersion: SCHEMA_VERSION,
    id: rec.id,
    date: rec.date,
    createdAt: rec.createdAt,
    updatedAt: rec.updatedAt,
    rev: rec.rev,
    deviceId: rec.deviceId,
    categoryId: rec.categoryId,
    projectId: rec.projectId ?? null,
    title: rec.title,
    body: rec.body,
    location: rec.location,
    media: rec.media,
  };
}

export function toCloudDaily(rec: DailyRecord | DailySummary): DailySummary {
  return {
    schemaVersion: SCHEMA_VERSION,
    date: rec.date,
    createdAt: rec.createdAt,
    updatedAt: rec.updatedAt,
    rev: rec.rev,
    deviceId: rec.deviceId,
    events: rec.events,
    good: rec.good,
    action: rec.action,
  };
}

export interface MonthIndex {
  schemaVersion: number;
  month: MonthKey;
  generatedAt: string;
  generatedBy: string;
  entries: Entry[];
  /** 一日のまとめも同梱する。新しい端末が1リクエストで揃えられるようにするため。 */
  dailies?: DailySummary[];
  /** その月のデイリータスク入力値。週次ぶんは月をまたぐため含めない。 */
  taskEntries?: TaskEntry[];
}

export function toCloudTaskEntry(rec: TaskEntryRecord | TaskEntry): TaskEntry {
  return {
    schemaVersion: SCHEMA_VERSION,
    kind: rec.kind,
    period: rec.period,
    createdAt: rec.createdAt,
    updatedAt: rec.updatedAt,
    rev: rec.rev,
    deviceId: rec.deviceId,
    values: rec.values,
  };
}

export function buildIndex(
  month: MonthKey,
  entries: EntryRecord[],
  dailies: DailyRecord[],
  taskEntries: TaskEntryRecord[],
  deviceId: string,
  generatedAt: string,
): MonthIndex {
  return {
    schemaVersion: SCHEMA_VERSION,
    month,
    generatedAt,
    generatedBy: deviceId,
    entries: [...entries]
      .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
      .map(toCloudEntry),
    dailies: [...dailies].sort((a, b) => (a.date < b.date ? -1 : 1)).map(toCloudDaily),
    taskEntries: [...taskEntries]
      .sort((a, b) => (a.period < b.period ? -1 : 1))
      .map(toCloudTaskEntry),
  };
}

/**
 * 競合解決（仕様書 §7.5）。updatedAt の新しい方が勝つ。
 * 同値の場合は deviceId の文字列比較で決める（どの端末でも同じ結果になるように）。
 */
export function pickWinner(a: Entry, b: Entry): { winner: Entry; loser: Entry } {
  if (a.updatedAt !== b.updatedAt) {
    return a.updatedAt > b.updatedAt ? { winner: a, loser: b } : { winner: b, loser: a };
  }
  return a.deviceId > b.deviceId ? { winner: a, loser: b } : { winner: b, loser: a };
}

export function toConflictDoc(loser: Entry, winner: Entry, detectedAt: string): unknown {
  return {
    ...toCloudEntry(loser),
    _conflict: {
      detectedAt,
      winnerUpdatedAt: winner.updatedAt,
      loserUpdatedAt: loser.updatedAt,
      loserDeviceId: loser.deviceId,
    },
  };
}

export interface TombstoneDoc {
  schemaVersion: number;
  month: MonthKey;
  items: {
    entryId: string;
    entryMonth: MonthKey;
    deletedAt: string;
    deviceId: string;
  }[];
}
